import { useCallback, useContext, useEffect, useState, useRef } from 'react'
import GeneratePdf from '../CareerNewPdf/GenerateNewPdf'
import AuthContext from '../../context/AuthContext';
import { Box, Typography, Dialog, DialogContent, DialogActions, Button } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useToast } from '../../hook/useToast';
import httpcommon from '../../helper/httpcommon';
import StatusCard from './StatusCard';
import { useNavigate } from 'react-router-dom';

const AssessmentPdf = () => {
    const [careerPdfData, setCareerPdfData] = useState(null);
    const [studentcareerreport, setStudentcareerreport] = useState(null);
    // phase: 'preparing' | 'generating' | 'uploading' | 'downloading' | 'done' | 'error'
    const [phase, setPhase] = useState('preparing');
    const [leaveDialog, setLeaveDialog] = useState({ open: false, href: null });
    const pendingNavRef = useRef(null);
    const generationStartedRef = useRef(false);
    const uploadStartedRef = useRef(false);
    const studentCareerReportRef = useRef(null);

    const user = useContext(AuthContext);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const studentUserId = user?.user?.userInfo?.user;

    const isBusy = phase !== 'done' && phase !== 'error';
    const phaseCopy = {
        preparing: {
            title: 'Preparing your report data',
            body: 'We are collecting your latest assessment answers and career recommendations. This can take a little time.',
        },
        generating: {
            title: 'Building your PDF',
            body: 'Your personalized report pages are being rendered in the browser. Please keep this tab open.',
        },
        uploading: {
            title: 'Saving to Reports',
            body: 'The PDF is ready and is being saved in your Reports section.',
        },
        downloading: {
            title: 'Preparing download',
            body: 'Your download is starting. The saved report will remain available in Reports.',
        },
        done: {
            title: 'Report saved',
            body: 'Your report is ready. You can download it here or open the Reports section.',
        },
        error: {
            title: 'Report could not be completed',
            body: 'Please try again. If it fails again, refresh after a minute and generate the report once more.',
        },
    };
    const currentPhaseCopy = phaseCopy[phase] || phaseCopy.preparing;

    // Warn before tab close / refresh
    useEffect(() => {
        if (!isBusy) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isBusy]);

    // Block in-app anchor navigation (sidebar links etc.) while busy
    useEffect(() => {
        if (!isBusy) return;
        const handleClick = (e) => {
            const anchor = e.target.closest('a');
            if (!anchor) return;
            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#') || anchor.target === '_blank') return;
            if (/^https?:\/\//.test(href) && !href.includes(window.location.host)) return;
            e.preventDefault();
            e.stopPropagation();
            pendingNavRef.current = () => window.location.assign(href);
            setLeaveDialog({ open: true, href });
        };
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [isBusy]);

    // Block browser back/forward while busy
    useEffect(() => {
        if (!isBusy) return;
        window.history.pushState({ assessmentGuard: true }, '', window.location.href);
        const handlePop = () => {
            window.history.pushState({ assessmentGuard: true }, '', window.location.href);
            pendingNavRef.current = () => window.history.back();
            setLeaveDialog({ open: true, href: 'back' });
        };
        window.addEventListener('popstate', handlePop);
        return () => window.removeEventListener('popstate', handlePop);
    }, [isBusy]);

    // Expose busy state for the parent Assessment header back button
    useEffect(() => {
        window.__assessmentBusy = isBusy;
        return () => { window.__assessmentBusy = false; };
    }, [isBusy]);

    const fetchPdfDataByStudentId = useCallback(async (filePath) => {
        try {
            const pdfUrl = `school/student/pdf-report`;
            const careerInfoUrl = `school/generate-career-info/`;

            const [pdfResponse, careerResponse] = await Promise.all([
                httpcommon.get(pdfUrl, { params: { preview: 1 } }),
                httpcommon.post(careerInfoUrl, {
                    "csv_path": filePath
                }),
            ]);

            const reportId = careerResponse?.data?.id || null;
            studentCareerReportRef.current = reportId;
            setStudentcareerreport(reportId);

            if (pdfResponse.status === 200 || careerResponse.status === 200) {
                const combinedData = {
                    pdf_report: pdfResponse?.data.pdf_report || {},
                    career_info: careerResponse?.data?.career_info || {},
                    school_board_name: careerResponse?.data?.school_board_name || "",
                };
                setCareerPdfData(combinedData);
            } else {
                setCareerPdfData(null);
                setPhase('error');
            }
        } catch (error) {
            if (error?.response?.status !== 404) {
                showToast(error.response?.data?.error || 'Error fetching data', 'error');
            }
            setCareerPdfData(null);
            setPhase('error');
        }
    }, [showToast]);

    const handleDownloadFeedbackCsv = useCallback(async () => {
        if (generationStartedRef.current) return;
        generationStartedRef.current = true;

        try {
            setPhase('preparing');
            const response = await httpcommon.get(
                `/school/download_feedback_binary_csv/${studentUserId}/`
            );

            if (response.status === 200 && response.data?.file_path) {
                await fetchPdfDataByStudentId(response.data.file_path);
            } else {
                setPhase('error');
            }
        } catch (error) {
            showToast(
                error?.response?.data?.error || "Error generating report",
                "error"
            );
            setPhase('error');
        }
    }, [fetchPdfDataByStudentId, showToast, studentUserId]);

    useEffect(() => {
        if (studentUserId) {
            handleDownloadFeedbackCsv();
        }
    }, [handleDownloadFeedbackCsv, studentUserId]);

    const handlePdfGenerated = async (pdfBlob, filename) => {
        if (uploadStartedRef.current) return;
        uploadStartedRef.current = true;

        try {
            const reportId = studentCareerReportRef.current || studentcareerreport;
            const formData = new FormData();
            formData.append('file', pdfBlob, filename);
            formData.append('filename', filename);
            formData.append('studentcareerreport_id', String(reportId));

            const response = await httpcommon.post(
                'school/student/documents/',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );

            if (response.status === 200 || response.status === 201) {
                setPhase('done');
            } else {
                setPhase('error');
            }
        } catch (error) {
            showToast(
                error?.response?.data?.error || 'Error uploading document',
                'error'
            );
            setPhase('error');
        }
    };

    const confirmLeave = () => {
        const proceed = pendingNavRef.current;
        pendingNavRef.current = null;
        setLeaveDialog({ open: false, href: null });
        setPhase('done'); // release guards so we don't re-block the navigation
        setTimeout(() => proceed && proceed(), 0);
    };

    const cancelLeave = () => {
        pendingNavRef.current = null;
        setLeaveDialog({ open: false, href: null });
    };

    return (
        <Box sx={{ position: "relative",overflow: 'hidden' }}>
            <Box
                component="img"
                src="/assets/newImages/thankyou.png"
                alt="Thank you"
                sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                }}
            />

            <Box sx={{
                position: "absolute",
                top: { xs: '4%', sm: 40 },
                left: { xs: '5%', sm: 60 },
                right: { xs: '5%', sm: 'auto' },
            }}>
                <Typography sx={{
                    color: "#292D5B",
                    fontWeight: 400,
                    fontSize: { xs: '28px', sm: '40px', md: '55px' },
                    lineHeight: 1.1,
                    mt: { xs: 0, sm: 1 },
                }}>
                    Thank You!
                </Typography>
                <Typography sx={{
                    color: "#231F20",
                    fontWeight: 700,
                    fontSize: { xs: '12px', sm: '16px' },
                    mt: { xs: 0.5, sm: 1 },
                }}>
                    Appreciate your dedication!
                </Typography>
                <Typography sx={{
                    color: "#231F20",
                    fontWeight: 400,
                    fontSize: { xs: '11px', sm: '16px' },
                    mt: { xs: 0.25, sm: 1 },
                    display: { xs: 'none', sm: 'block' },
                }}>
                    {currentPhaseCopy.title}
                </Typography>
                <Typography sx={{
                    color: "#231F20",
                    fontWeight: 400,
                    fontSize: { xs: '11px', sm: '16px' },
                    mt: { xs: 0.25, sm: 1 },
                    display: { xs: 'none', sm: 'block' },
                }}>
                    {currentPhaseCopy.body}
                </Typography>

                <Box sx={{
                    mt: { xs: 1, sm: 3 },
                    transform: { xs: 'scale(0.7)', sm: 'none' },
                    transformOrigin: 'top left',
                }}>
                    <StatusCard phase={phase} />
                </Box>
                <Typography sx={{
                    color: phase === 'error' ? '#B42318' : 'rgba(41, 45, 91, 0.76)',
                    fontWeight: 600,
                    fontSize: { xs: '11px', sm: '13px' },
                    mt: { xs: 0.5, sm: 1.5 },
                    maxWidth: 420,
                }}>
                    {currentPhaseCopy.body}
                </Typography>
                {phase === 'done' && (
                    <Button
                        variant="contained"
                        onClick={() => navigate('/reports')}
                        sx={{
                            mt: { xs: 1, sm: 2 },
                            backgroundColor: '#292D5B',
                            borderRadius: '10px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            '&:hover': { backgroundColor: '#1F2347' },
                        }}
                    >
                        View Reports
                    </Button>
                )}
            </Box>

            <Box sx={{
                position: "absolute",
                bottom: { xs: '6%', sm: 60 },
                left: { xs: '5%', sm: 60 },
                right: { xs: '5%', sm: 'auto' },
                transform: { xs: 'scale(0.8)', sm: 'none' },
                transformOrigin: 'bottom left',
            }}>
         <GeneratePdf
                    careerPdfData={careerPdfData}
                    onPdfGenerated={handlePdfGenerated}
                    phase={phase}
                    setPhase={setPhase}
                />
            </Box>
       
            <Dialog
                open={leaveDialog.open}
                maxWidth="xs"
                fullWidth
                onClose={cancelLeave}
                PaperProps={{
                    sx: {
                        borderRadius: '18px',
                        p: 1,
                        boxShadow: '0 24px 60px rgba(20, 25, 70, 0.25)',
                        overflow: 'hidden',
                    },
                }}
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(41, 45, 91, 0.45)' },
                }}
            >
                <DialogContent sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(245, 166, 35, 0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <WarningAmberRoundedIcon sx={{ color: '#F5A623', fontSize: 32 }} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#292D5B',
                            mb: 1,
                        }}
                    >
                        Your report is still being prepared
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: 'rgba(41, 45, 91, 0.7)',
                            lineHeight: 1.6,
                            px: 1,
                        }}
                    >
                        Leaving now will cancel the process and you may need to start over.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
                    <Button
                        onClick={confirmLeave}
                        disableElevation
                        sx={{
                            textTransform: 'none',
                            borderRadius: '10px',
                            px: 2.5,
                            py: 1,
                            fontWeight: 500,
                            color: 'rgba(41, 45, 91, 0.65)',
                            '&:hover': { backgroundColor: 'rgba(41, 45, 91, 0.06)' },
                        }}
                    >
                        Leave anyway
                    </Button>
                    <Button
                        onClick={cancelLeave}
                        variant="contained"
                        disableElevation
                        sx={{
                            textTransform: 'none',
                            borderRadius: '10px',
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            backgroundColor: '#292D5B',
                            '&:hover': { backgroundColor: '#1F2347' },
                        }}
                    >
                        Stay on this page
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AssessmentPdf;
